import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FilterBar } from '../components/common/FilterBar'
import { CarFront, ShieldAlert, X } from 'lucide-react'
import { useGetFleet } from '../hooks/backend/fleet'
import { useCapabilities } from '../hooks/useCapabilities'
import { useRowSelection } from '../hooks/useRowSelection'
import { Spinner } from '../lib/shadcn/spinner'
import { Switch } from '../lib/shadcn/switch'
import { Label } from '../lib/shadcn/label'
import { Button } from '../lib/shadcn/button'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { FleetList } from '../components/fleet/FleetList'
import { BulkEditDialog } from '../components/fleet/BulkEditDialog'
import { type FleetFilters, readFleetFilters, writeFleetFilters } from '../utils/fleetFilters'
import {
  type FleetRow,
  type LocationBucket,
  LIFECYCLE_STATUS_OPTIONS,
  LOCATION_BUCKETS,
  locationBucket,
  locationTone,
} from '../utils/fleet'

type LifecycleTab = 'all' | string

const TABS: { value: LifecycleTab; label: string }[] = [
  { value: 'all', label: 'All' },
  ...LIFECYCLE_STATUS_OPTIONS.map((o) => ({ value: o.code, label: o.label })),
]

export default function Fleet() {
  const { loading: accessLoading, canView, canEditFleet, caption } = useCapabilities()
  // Every filter on this page lives in the query string, not in component state.
  // Platform request #5: opening a car and coming back reset the search and every
  // filter. The URL is part of the history entry, so Back restores it, a refresh
  // keeps it and a filtered list can be shared. The top bar search still opens
  // this page with ?q= already typed in; it is the same key. See
  // utils/fleetFilters.ts for why sessionStorage was not used instead.
  //
  // Writes use replace, not push. One history entry per keystroke or chip click
  // would make Back walk through every intermediate filter before leaving the
  // page, which is its own annoyance.
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => readFleetFilters(params, LOCATION_BUCKETS), [params])
  const { q: search, tab, activeOnly, generalFleetOnly } = filters
  const selectedLocations = filters.locations as LocationBucket[]
  const update = useCallback(
    (patch: Partial<FleetFilters>) =>
      setParams((prev) => writeFleetFilters(prev, patch, LOCATION_BUCKETS), { replace: true }),
    [setParams],
  )
  const setSearch = useCallback((q: string) => update({ q }), [update])
  const setTab = useCallback((t: LifecycleTab) => update({ tab: t }), [update])
  const setActiveOnly = useCallback((v: boolean) => update({ activeOnly: v }), [update])
  const setGeneralFleetOnly = useCallback((v: boolean) => update({ generalFleetOnly: v }), [update])
  const setSelectedLocations = useCallback(
    (next: LocationBucket[] | ((prev: LocationBucket[]) => LocationBucket[])) =>
      update({ locations: typeof next === 'function' ? next(selectedLocations) : next }),
    [update, selectedLocations],
  )

  const fleet = useGetFleet()
  const trigger = fleet.trigger

  // Fetch by lifecycle server-side; location and operational are filtered
  // client-side so tab and funnel counts stay accurate across all locations.
  const statuses = useMemo(() => (tab === 'all' ? [] : [tab]), [tab])

  const load = useCallback(() => {
    trigger({ statuses, location: null, operational: null }, { skipCache: true })
  }, [trigger, statuses])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const allRows = (fleet.data as FleetRow[] | undefined) ?? []

  // Tab counts computed from the fetched set for the active tab.
  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allRows.length }
    for (const r of allRows) {
      counts[r.lifecycle_status] = (counts[r.lifecycle_status] ?? 0) + 1
    }
    return counts
  }, [allRows])

  // Funnel: location bucket counts for the active tab (before client filters).
  const funnel = useMemo(() => {
    const byBucket = new Map<LocationBucket, number>()
    for (const r of allRows) {
      const b = locationBucket(r.location)
      byBucket.set(b, (byBucket.get(b) ?? 0) + 1)
    }
    return LOCATION_BUCKETS.map((b) => ({ bucket: b, count: byBucket.get(b) ?? 0 }))
  }, [allRows])

  // Apply location + operational + search filters client-side.
  const vehicles = useMemo(() => {
    const term = search.trim().toLowerCase()
    return allRows.filter((r) => {
      if (generalFleetOnly && r.in_general_fleet !== true) return false
      if (selectedLocations.length > 0 && !selectedLocations.includes(locationBucket(r.location)))
        return false
      if (activeOnly && r.is_active !== true) return false
      if (term) {
        const hay = [r.registration_number, r.owner_name, r.make, r.model]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!hay.includes(term)) return false
      }
      return true
    })
    // Active first, then alphabetical inside each group. The registration is the
    // only handle anybody uses to find a car, so within a group it is the sort
    // that lets somebody scan for one rather than hunt.
    .sort((a, b) => {
      const aActive = a.is_active === true
      const bActive = b.is_active === true
      if (aActive !== bActive) return aActive ? -1 : 1
      return (a.registration_number ?? '').localeCompare(b.registration_number ?? '')
    })
  }, [allRows, selectedLocations, activeOnly, generalFleetOnly, search])

  // Active is the derived answer from v_fleet_status, not a status anybody typed:
  // in the general fleet, no hold, operational, standing at Operating. It is
  // counted over the rows on screen so the number always matches the list under
  // it rather than describing a fleet the operator is not looking at.
  const activeCount = useMemo(() => vehicles.filter((r) => r.is_active === true).length, [vehicles])

  const selection = useRowSelection(vehicles, (v) => v.vehicle_id)

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            The Fleet view is available to admins only.
          </p>
          <p className="text-xs text-muted-foreground">
            Contact an admin if you need access to fleet status.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <CarFront className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Fleet</h1>
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
              <Switch
                id="general-fleet-only"
                checked={generalFleetOnly}
                onCheckedChange={setGeneralFleetOnly}
              />
              <Label htmlFor="general-fleet-only" className="cursor-pointer text-xs font-normal">
                General fleet only
              </Label>
            </div>
            <span className="text-xs text-muted-foreground tabular-nums">
              {fleet.loading
                ? '…'
                : `${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} · ${activeCount} active`}
            </span>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

        {/* Lifecycle tabs */}
        <div className="mt-3 inline-flex max-w-full flex-wrap rounded-md border border-border bg-background p-0.5">
          {TABS.map((t) => {
            const active = tab === t.value
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={cn(
                  'flex items-center gap-1.5 rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                  active
                    ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
                <span className="tabular-nums opacity-70">{tabCounts[t.value] ?? 0}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Funnel: location breakdown for the active tab, and the location filter.
          It was a read-only strip and a separate dropdown said the same words a
          few pixels below it. Two controls for one idea is how people learn to
          ignore the one that does nothing. Clicking a chip toggles it; clicking a
          selected chip clears it. */}
      {allRows.length > 0 && (
        <FilterBar
          label="Locations"
          activeCount={selectedLocations.length}
          summary={selectedLocations.join(', ')}
          trailing={`${allRows.length} vehicles`}
        >
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
          {funnel.map((f) => {
            const on = selectedLocations.includes(f.bucket)
            return (
              <button
                key={f.bucket}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setSelectedLocations((prev) =>
                    prev.includes(f.bucket)
                      ? prev.filter((b) => b !== f.bucket)
                      : [...prev, f.bucket],
                  )
                }
                className={cn(
                  'rounded-full transition-shadow',
                  on
                    ? 'ring-2 ring-[hsl(var(--brand))] ring-offset-1 ring-offset-background'
                    : 'opacity-90 hover:opacity-100',
                )}
              >
                <Chip tone={locationTone(f.bucket === 'Unknown' ? null : f.bucket)}>
                  {f.bucket}
                  <span className="ml-1 tabular-nums font-semibold">{f.count}</span>
                </Chip>
              </button>
            )
          })}
          {selectedLocations.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-2 text-xs"
              onClick={() => setSelectedLocations([])}
            >
              <X className="h-3 w-3" />
              Clear location filter
            </Button>
          )}
        </div>
        </FilterBar>
      )}

      {/* Selection toolbar */}
      {canEditFleet && selection.count > 0 && (
        <div className="flex items-center gap-3 border-b border-border bg-[hsl(var(--brand-soft))] px-4 py-2 text-[hsl(var(--brand-soft-foreground))]">
          <span className="text-sm font-medium tabular-nums">{selection.count} selected</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => selection.clear()}
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
          <div className="ml-auto">
            {/* The selection is deliberately NOT cleared here. An operator who has
                filtered down to a set and ticked it almost always wants to make the
                next change to the same set; clearing it made every second change a
                rebuild of the selection. Luyanda, 22 August 2026. */}
            <BulkEditDialog selectedRows={selection.selectedRows} onDone={load} />
          </div>
        </div>
      )}

      {/* List */}
      <FleetList
        vehicles={vehicles}
        loading={fleet.loading}
        error={fleet.error}
        search={search}
        onSearchChange={setSearch}
        activeOnly={activeOnly}
        onActiveOnlyChange={setActiveOnly}
        canEditFleet={canEditFleet}
        onReload={load}
        selection={selection}
      />
    </div>
  )
}
